import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { useModalsContext } from '../../providers/ModalsProvider/Modals.context.ts';
import { useChatContext } from './Chat.hook.ts';
import './Chat.styles.scss';
import { tChatProps } from './Chat.types.ts';
import ChatInfo from './components/ChatInfo/ChatInfo.component.tsx';
import ChatMenu from './components/ChatMenu/ChatMenu.component.tsx';
import LeadInfo from './components/LeadInfo/LeadInfo.component.tsx';
import MessageBar from './components/MessageBar/MessageBar.component.tsx';
import MessageContainer from './components/MessageContainer/MessageContainer.component.tsx';
import ChatSearchBar from './components/ChatSearch/ChatSearchBar.component.tsx';
import { useChatSearchContext } from './components/ChatSearch/ChatSearch.context.tsx';
import useChatSearchPresence from './components/ChatSearch/ChatSearchPresence.hook.ts';
import useChatSearchShortcut from './components/ChatSearch/ChatSearchShortcut.hook.ts';

export default function ChatContent(props: tChatProps) {
  const chatRef = useRef<HTMLDivElement>(null);
  const conversationWrapperRef = useRef<HTMLDivElement>(null);
  const [messagesMaxHeight, setMessagesMaxHeight] = useState<number>(0);
  // Static header height (50px from grid) plus any modal extras
  let staticContentHeight = 50;
  let containerRef = conversationWrapperRef;
  const { maximizeModal, chatModalIsOpen } = useModalsContext();
  const { chatInputContainerRef } = useChatContext();
  // SRE-3328: null with useMessageSearchEnabled off, and then nothing below
  // changes.
  const chatSearch = useChatSearchContext();
  useChatSearchShortcut(chatRef);
  const searchPresence = useChatSearchPresence(chatSearch);

  if (props.isChatModal) {
    containerRef = chatRef;

    if (chatModalIsOpen && !maximizeModal) {
      staticContentHeight = 122; // modal adds extra chrome beyond header
    }
  }

  // Use a ref so the ResizeObserver callback always reads the latest values
  const calcRef = useRef<() => void>(() => {});
  calcRef.current = () => {
    if (!containerRef.current) return;
    const conversationWrapperHeight = containerRef.current.offsetHeight;
    // MessageBar is absolutely positioned, so its wrapper has 0 natural height.
    // Query the .MessageBar element directly to get its true rendered height.
    const messageBarEl = chatInputContainerRef.current?.querySelector(
      '.MessageBar',
    ) as HTMLElement | null;
    const inputHeight = messageBarEl?.offsetHeight ?? 50;
    const messagesWrapperHeight =
      conversationWrapperHeight - staticContentHeight - inputHeight;

    setMessagesMaxHeight(messagesWrapperHeight);
  };

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => calcRef.current());

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    // MessageBar is absolutely positioned — observe the element itself, not the wrapper
    const messageBarEl = chatInputContainerRef.current?.querySelector('.MessageBar');
    if (messageBarEl) {
      resizeObserver.observe(messageBarEl);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <>
      <div className="Chat" ref={chatRef}>
        <div className="Chat__Header">
          <LeadInfo {...props} />
        </div>
        <div className="Chat__Conversation">
          <div
            className="Chat__Conversation__Wrapper"
            ref={conversationWrapperRef}
          >
            <div className="Chat__Conversation__Wrapper__Header">
              {/* SRE-3328: while the search is open it takes this row, at the
                  chat column's width, and the header above stays as it is.
                  ChatMenu stays mounted (its dropdowns and block modal keep
                  their state) but out of sight and inert under the bar, and
                  laid out, so the bar can grow out of its magnifier. */}
              <div
                ref={searchPresence.menuRef}
                className={classNames(
                  'Chat__Conversation__Wrapper__Header__Menu',
                  { 'opacity-0': searchPresence.menuHidden },
                )}
                {...(searchPresence.menuInert ? { inert: '' } : {})}
              >
                <ChatMenu />
              </div>
              {searchPresence.barMounted ? (
                <ChatSearchBar
                  leaving={searchPresence.leaving}
                  onLeft={searchPresence.onBarLeft}
                />
              ) : null}
            </div>
            <div
              className="Chat__Conversation__Wrapper__Messages"
              style={{
                maxHeight: messagesMaxHeight
                  ? `${messagesMaxHeight}px`
                  : 'auto',
              }}
            >
              <MessageContainer />
            </div>

            <div
              className="Chat__Conversation__Wrapper__TextInput"
              ref={chatInputContainerRef}
            >
              <MessageBar />
            </div>
          </div>

          <div className="Chat__Conversation__Attributes">
            <ChatInfo />
          </div>
        </div>
      </div>
    </>
  );
}
